import {Link} from "react-router-dom";
import classNames from "classnames";
import {Fragment, ReactNode, useCallback, useState} from "react";
import {UserName} from "../common/UserName";
import {Chat, Group, SidebarItem, WorkspaceMember} from "../../lib/types";
import {GroupName} from "../common/GroupName";
import {Breadcrumbs} from "../common/Breadcrumbs";
import {Icon} from "../common/Icon";
import Dropdown from "../common/Dropdown";
import {Button} from "../common/Button";
import {pinToGroup, unpinFromGroup} from "../../lib/database";
import {formatName} from "../../lib/utils/formatting";
import {timeDifference} from "../../lib/utils/time";
import {useUserContext} from "../../providers/UserContext";
import {useChatListContext} from "../../providers/ChatListProvider";

interface ChatListItemProps {
  chat: Chat;
  canPin?: boolean;
  activeDropdown?: string | null;
  setActiveDropdown: (chatId: string | null) => void;
  now: number;
  content?: ReactNode
  pinned?: boolean
}
export const ChatListItem = ({ chat, canPin, activeDropdown, setActiveDropdown, now, content, pinned }: ChatListItemProps) => {
  const {unreadChats, activeChatId, groups, activeWorkspace, user, getMember, sidebar} = useUserContext();
  const {isUnread, currentFilter} = useChatListContext();

  const hasUnreadMention = Object.values(unreadChats.find(unread => unread.id === chat.id)?.messages || {}).some(
    message => message.mentions && message.mentions.includes(user?.uid || "")
  );
  const breadcrumbs = (chat?.parents || []).map(chat => ({
    id: chat.id,
    title: chat.title
  }));
  breadcrumbs.push({
    id: chat?.id || "",
    title: chat?.title || ""
  })
  const isThisUnread = isUnread(chat);
  const mainColor = isThisUnread ? "text-black" : "text-gray-3";
  const secondaryColor = isThisUnread ? "text-gray-1" : "text-gray-4";
  const [topPos, setTopPos] = useState(0);
  const getRecipients = useCallback((chat: Chat) => {
    let sidebarItem: null | SidebarItem = null;
    if (currentFilter?.startsWith("sidebar_")) {
      const sidebarId = currentFilter.replace("sidebar_", "");
      if (sidebarId) {
        sidebarItem = sidebar.filter(item => item.id === sidebarId)[0];
      }
    }
    const recipients: {
      type: "group" | "user",
      id: string,
      name: string,
      group?: Group,
      user?: WorkspaceMember,
    }[] = [];
    const membersToFilterOut: string[] = [];
    let isThereAPublicGroup = false;
    Object.keys(chat.groupPermissions || {}).forEach(groupId => {
      const group = groups.find(group => group.id === groupId);
      if (group) {
        recipients.push({
          type: "group",
          id: group.id,
          name: group.name,
          group,
        });
        if(group.visibility === "public") {
          isThereAPublicGroup = true;
        }
        membersToFilterOut.push(...Object.keys(group.permissions));
      }
    });
    if (activeWorkspace && !isThereAPublicGroup) {
      Object.keys(chat.userPermissions || {}).forEach(userId => {
        const user = activeWorkspace.members[userId];
        if (chat.authorId !== user.id && !membersToFilterOut.includes(user.id)) {
          recipients.push({
            type: "user",
            id: user.id,
            name: user.displayName,
            user,
          });
        }
      });
    }
    const author = getMember(chat.authorId);
    if (author && !isThereAPublicGroup && !membersToFilterOut.includes(author.id)) {
      recipients.push({
        type: "user",
        id: author.id,
        name: author.displayName,
        user: author,
      })
    }
    return recipients.filter(recipient => {
      return recipient.id !== user?.uid &&
        (!sidebarItem || !sidebarItem.meaningfulGroups?.includes(recipient.id)) &&
        (!sidebarItem || !sidebarItem.meaningfulUsers?.includes(recipient.id))
    });

  }, [groups, activeWorkspace, user, currentFilter, sidebar]);
  const recipients = getRecipients(chat);
  const getUrl = useCallback((chat: Chat) => {
    if(currentFilter?.startsWith("inbox")) {
      return "/inbox/chat/" + chat.id;
    }
    if(currentFilter?.startsWith("group")) {
      return "/group/" + currentFilter.replace("group_", "") + "/chat/" + chat.id;
    }
    if(currentFilter?.startsWith("sidebar")) {
      return "/chats/" + currentFilter.replace("sidebar_", "") + "/chat/" + chat.id;
    }
    return "/chat/" + chat.id;
  },[currentFilter]);
  return (
    <Link
      key={chat.id}
      to={getUrl(chat)}
      className={classNames(
        "flex w-full relative items-center justify-between gap-4 transition-all border-t border-t-gray-7 last:border-b last:border-b-gray-7 group",
        activeChatId === chat.id ? " border-gray-7" : "bg-transparent ",
      )}
    >
      <div className={classNames(
        "flex relative flex-col leading-1 transition-all border border-transparent hover:bg-white w-[calc(100%-24px)] rounded-md px-[12px] mx-[12px] py-[8px] my-[8px]",
        activeChatId === chat.id && "shadow-elevation-2 bg-white"
      )}>
        <div className={"font-style-body-sm text-gray-3 flex w-full overflow-x-hidden"}>
                           <span className="flex-grow inline-flex gap-1 flex-nowrap text-nowrap overflow-ellipsis">
                             {recipients.length > 0 && currentFilter?.startsWith("sidebar") && "Includes "}
                             {recipients.map((recipient, index) => {
                               return (
                                 <Fragment key={recipient.user?.id || recipient.group?.id}>
                                   {index > 0 && 'and '}
                                   {recipient.type === "user" ? (
                                     <UserName key={recipient.user?.id} user={recipient.user as WorkspaceMember}/>) : (
                                     <GroupName key={recipient.group?.id} group={recipient.group as Group}/>)}
                                 </Fragment>
                               )
                             })

                             }
                          </span>
        </div>
        <div className={"w-full items-center flex breadcrumbs max-w-full"}>
          <div className="flex-grow flex gap-1 items-center">
            {pinned && <Icon name={"keep"} size="xsmall"/>}
            <span className={"font-style-label-md line-clamp-1 " + mainColor}>
              {chat.title}
            </span>
          </div>
          {canPin && (<div className={"relative"}>
            <button onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveDropdown(chat.id)
              const rect = e.currentTarget.getBoundingClientRect();
              setTopPos(rect.bottom);
            }} className={"opacity-0 transition-all group-hover:opacity-100"}>
              <Icon name={"more_vert"}
                                                                                    size="xsmall"/>
            </button>
            <div className={classNames(
              "fixed z-10 -mt-2 pointer-events-none transition-[opacity] w-[100px]",
              activeDropdown === chat.id ? "opacity-100 pointer-events-auto" : "opacity-0"
            )} style={
              {
                top: topPos + "px",
              }
            }>
              <Dropdown onClose={() => {
                setActiveDropdown(null)
              }}
                        open={activeDropdown === chat.id}
              >
                <Button variant={"tertiary"} className={"w-full !justify-start pointer"}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (pinned) {
                            unpinFromGroup(currentFilter?.replace("group_", "").replace("sidebar_", "") || "", chat.id);
                          } else {
                            pinToGroup(currentFilter?.replace("group_", "").replace("sidebar_", "") || "", chat.id);
                          }
                          setActiveDropdown(null);
                        }}>{pinned ? "Unpin" : "Pin"}</Button>
              </Dropdown>
            </div>
          </div>)
          }
          {
            hasUnreadMention && (
              <span
                className="red-dot rounded-full h-[6px] w-[6px] ml-1 flex justify-center items-center bg-danger-2 text-white"></span>
            )
          }
        </div>
        <div className={classNames(
          "font-style-body-md mt-1 flex justify-between ",
          mainColor
        )}>
          {content ? content : (
            <>
              <div className="w-[calc(100%-70px)]">
                {chat.lastMessage?.deleted
                  ? <span>This message was deleted</span>
                  : <span className="line-clamp-1">
                              <span className={"font-500 mr-1"}>
                                {formatName(chat.lastMessage?.authorName || "")}:
                              </span>
                              {chat.lastMessage?.plainText || ""}
                            </span>
                }
              </div>
              <div className={"w-[50px] ml-auto text-right font-style-body-sm " + secondaryColor}
                   title={new Date(chat.lastMessage?.timestamp || 0).toLocaleString()}
              >
                {chat.lastMessage && timeDifference(now, chat.lastMessage.timestamp)}
              </div>
             </>
          )}
        </div>
      </div>
    </Link>
  )
}